import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Progress, Typography } from 'antd';
import {
  ChevronRight as ChevronRightIcon,
  CircleCheck as CircleCheckIcon,
  Rocket as RocketIcon,
  X as XIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router';

const { Text } = Typography;

export interface GettingStartedStep {
  key: string;
  label: string;
  description: string;
  done: boolean;
  /** Route to deep-link to when the step is incomplete. */
  to: string;
  /** Optional CTA label override (defaults to "Get started"). */
  cta?: string;
}

interface GettingStartedChecklistProps {
  steps: GettingStartedStep[];
  /** localStorage key so dismissal persists per browser. */
  storageKey: string;
}

function loadDismissed(storageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

/**
 * Dismissible activation checklist. Reads REAL completion flags from the
 * caller, deep-links each incomplete step, and persists dismissal in
 * localStorage. Auto-hides once every step is complete so finished orgs
 * never see it again.
 */
export function GettingStartedChecklist({ steps, storageKey }: GettingStartedChecklistProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => loadDismissed(storageKey));

  const completedCount = useMemo(() => steps.filter((s) => s.done).length, [steps]);
  const allDone = completedCount === steps.length;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // Quota / private-mode errors are non-fatal — the checklist just
      // re-appears next load, which is acceptable.
    }
  };

  // Hide once dismissed or fully complete (no value in nagging finished orgs).
  if (dismissed || allDone) return null;

  const percent = Math.round((completedCount / steps.length) * 100);

  return (
    <Card
      size="small"
      style={{ marginBottom: 20, borderLeft: '3px solid var(--color-primary)' }}
      styles={{ body: { padding: 16 } }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Text strong style={{ fontSize: 15 }}>
            <RocketIcon style={{ color: 'var(--color-primary)', marginRight: 8 }} />
            {t('dashboard.gettingStarted')}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('dashboard.stepsComplete', { completed: completedCount, total: steps.length })}
            </Text>
          </div>
        </div>
        <button
          type="button"
          className="dashboard-section-link"
          aria-label={t('dashboard.dismissChecklist')}
          onClick={handleDismiss}
          style={{ flexShrink: 0 }}
        >
          <XIcon />
        </button>
      </div>

      <Progress percent={percent} showInfo={false} style={{ marginBottom: 12 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {steps.map((step) => (
          <button
            key={step.key}
            type="button"
            disabled={step.done}
            onClick={() => {
              if (!step.done) void navigate(step.to);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 8px',
              border: 'none',
              borderRadius: 8,
              background: 'transparent',
              textAlign: 'left',
              cursor: step.done ? 'default' : 'pointer',
              width: '100%',
            }}
          >
            <span style={{ flexShrink: 0, fontSize: 18, lineHeight: 1 }}>
              {step.done ? (
                <CircleCheckIcon style={{ color: 'var(--color-success)' }} />
              ) : (
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid var(--color-border)',
                  }}
                />
              )}
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <Text
                strong={!step.done}
                delete={step.done}
                type={step.done ? 'secondary' : undefined}
                style={{ display: 'block', fontSize: 14 }}
              >
                {step.label}
              </Text>
              {!step.done && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {step.description}
                </Text>
              )}
            </span>
            {!step.done && (
              <span style={{ flexShrink: 0, color: 'var(--color-primary)', fontSize: 13 }}>
                {step.cta ?? t('common.getStarted')} <ChevronRightIcon style={{ fontSize: 12 }} />
              </span>
            )}
          </button>
        ))}
      </div>
    </Card>
  );
}

export default GettingStartedChecklist;
